import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { getMonthRange, HttpHelper } from '@umss/shared/utils';
import { map, Observable, Subject, tap } from 'rxjs';
import {
  FrequentShiftTimesDto,
  PagedResultDto,
  Shift,
  ShiftCancelRequest,
  ShiftCheckConflictRequest,
  ShiftSearchDto,
  ShiftUpcomingRequest
} from '../../models';

@Injectable({ providedIn: 'root' })
export class ShiftsService {
  private readonly apiUrl = '/api/Shifts';

  readonly clockedInShift$ = new Subject<void>();

  constructor(private http: HttpClient) {}

  checkShiftConflicts(
    request: ShiftCheckConflictRequest
  ): Observable<PagedResultDto<Shift>> {
    const params = HttpHelper.toHttpParams(request);
    return this.http.get<PagedResultDto<Shift>>(
      `${this.apiUrl}/check-conflicts`,
      { params }
    );
  }

  getShifts(request?: ShiftSearchDto): Observable<PagedResultDto<Shift>> {
    const params = HttpHelper.toHttpParams(request ?? {});

    return this.http.get<PagedResultDto<Shift>>(`${this.apiUrl}`, { params });
  }

  getShiftById(id: string): Observable<Shift> {
    return this.http.get<Shift>(`${this.apiUrl}/${id}`);
  }

  getUpcomingShifts(
    payload?: Partial<ShiftUpcomingRequest>
  ): Observable<Shift[]> {
    const params = HttpHelper.toHttpParams(payload ?? {});
    return this.http.get<Shift[]>(`${this.apiUrl}/upcoming`, { params });
  }

  getContractorShiftById(id: string): Observable<Shift> {
    return this.http.get<Shift>(`/api/Contractors/me/shifts/${id}`);
  }

  getContractorCurrentShift(): Observable<Shift | null> {
    return this.getContractorShifts({ statuses: ['InProgress'] }).pipe(
      map((shifts) => (shifts.items.length === 1 ? shifts.items[0] : null))
    );
  }

  getContractorShifts(
    request?: ShiftSearchDto
  ): Observable<PagedResultDto<Shift>> {
    const params = HttpHelper.toHttpParams(request ?? {});
    return this.http.get<PagedResultDto<Shift>>('/api/Contractors/me/shifts', {
      params
    });
  }

  cancelShift(id: string, payload: ShiftCancelRequest): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/${id}/cancel`, payload);
  }

  cancelContractorShift(
    id: string,
    payload: ShiftCancelRequest
  ): Observable<void> {
    return this.http.post<void>(
      `/api/Contractors/me/shifts/${id}/cancel`,
      payload
    );
  }

  startShift(id: string, note?: string): Observable<Shift> {
    return this.http
      .post<Shift>(`/api/Contractors/me/shifts/${id}/start`, { note })
      .pipe(tap(() => this.clockedInShift$.next()));
  }

  completeShift(id: string, note?: string): Observable<Shift> {
    return this.http
      .post<Shift>(`/api/Contractors/me/shifts/${id}/complete`, { note })
      .pipe(tap(() => this.clockedInShift$.next()));
  }

  getContractorShiftsForMonth(
    contractorId: string,
    year: number,
    month: number
  ): Observable<Shift[]> {
    const { startDate, endDate } = getMonthRange(year, month);

    const searchDto: ShiftSearchDto = {
      contractorId,
      startDate,
      endDate,
      pageSize: 100 // Get all shifts for the month (unlikely to exceed 100)
    };

    return this.getShifts(searchDto).pipe(
      map((response: PagedResultDto<Shift>) => response.items || [])
    );
  }

  /**
   * Get the most frequently used shift start and end times for the current contractor.
   * Analyzes completed shifts from the last 180 days, with shifts from the last 30 days weighted 50% higher.
   * **Note: This endpoint should only be used by users with contractor role.**
   * @param topCount Optional number of time pairs to return (default: 3, max: 10).
   * @returns An Observable of FrequentShiftTimesDto containing the most frequent shift time pairs.
   */
  getContractorFrequentShiftTimes(
    topCount?: number
  ): Observable<FrequentShiftTimesDto> {
    const params = topCount ? HttpHelper.toHttpParams({ topCount }) : undefined;
    return this.http.get<FrequentShiftTimesDto>(
      `${this.apiUrl}/me/frequent-shift-times`,
      { params }
    );
  }

  /**
   * Get the most frequently used shift start and end times for a specific facility.
   * Analyzes all completed shifts at the facility from the last 180 days, with shifts from the last 30 days weighted 50% higher.
   * **Note: This endpoint should only be used by users with facility admin role.**
   * @param facilityId The ID of the facility to get frequent shift times for.
   * @param topCount Optional number of time pairs to return (default: 3, max: 10).
   * @returns An Observable of FrequentShiftTimesDto containing the most frequent shift time pairs.
   */
  getFacilityFrequentShiftTimes(
    facilityId: string,
    topCount?: number
  ): Observable<FrequentShiftTimesDto> {
    const params = topCount ? HttpHelper.toHttpParams({ topCount }) : undefined;
    return this.http.get<FrequentShiftTimesDto>(
      `/api/facilities/${facilityId}/frequent-shift-times`,
      { params }
    );
  }

  updateClockInOutTimes(
    shiftId: string,
    actualClockIn: string,
    actualClockOut: string
  ): Observable<Shift> {
    return this.http.put<Shift>(`${this.apiUrl}/${shiftId}/clock-times`, {
      actualClockIn,
      actualClockOut
    });
  }
}
